import type { DuplicateDecisionRequest, LeadCreateRequest } from "./types";

/**
 * Offline store (TASK-004): a simple localStorage-backed queue for mutations
 * plus a local index of leads/drafts captured on the device. TanStack Query
 * runs with networkMode "offlineFirst" (see main.tsx); when the device is
 * offline the field screens enqueue the mutation here instead and replay it
 * on the next `online` event.
 *
 * NOTE: FRONTEND worker rules prefer IndexedDB; localStorage is used here as
 * the simplest synchronous store for small JSON payloads (no photo bytes —
 * only file names are kept). Upgrade path: swap storage adapter.
 */

const QUEUE_KEY = "pg_field_queue_v1";
const LEADS_KEY = "pg_field_leads_v1";

// ── Local lead index ─────────────────────────────────────────────────────────

export type LocalLeadStatus =
  | "draft" // saved on device only (e.g. geofence-blocked draft)
  | "pending-sync" // create-lead queued, not yet on the server
  | "synced" // created on the server
  | "pending-duplicate-check" // synced; duplicate check queued for sync
  | "kyc-submitted"
  | "closed-merge"
  | "closed-reject";

/** Per-photo upload lifecycle (TASK-007b). */
export type PhotoUploadState = "pending" | "uploading" | "failed" | "uploaded";

export interface PhotoCapture {
  name: string;
  state: PhotoUploadState;
  /** Server-relative url once uploaded ("/uploads/<file>"). */
  url?: string;
}

export type PhotoWhich = "customer" | "house";

export interface LeadPhotos {
  customer?: PhotoCapture;
  house?: PhotoCapture;
}

export interface LocalLeadRecord {
  /** Device-local id ("local-<uuid>"); used in routes before the server id exists. */
  localId: string;
  /** Server lead id once created. */
  serverId?: number;
  name: string;
  phone?: string;
  ward?: string;
  status: LocalLeadStatus;
  createdAt: string;
  /** Form payload for drafts / queued creates. */
  formDraft?: LeadCreateRequest;
  /** Photo metadata + upload state. File bytes live in the in-memory
      registry below (see limitation note). */
  photos?: LeadPhotos;
  /** Duplicate decision recorded for this lead (for the S4 summary row). */
  duplicateDecision?: { decision: "merge" | "reject" | "continue"; note: string; recordedAt: string };
}

// ── Mutation queue ────────────────────────────────────────────────────────────

export type QueuedMutation =
  | {
      queueId: string;
      kind: "create-lead";
      localId: string;
      payload: LeadCreateRequest;
      createdAt: string;
    }
  | {
      queueId: string;
      kind: "duplicate-decision";
      /** Server lead id, or localId when the create is still queued. */
      leadRef: string;
      payload: DuplicateDecisionRequest;
      createdAt: string;
    }
  | {
      queueId: string;
      kind: "duplicate-check";
      leadRef: string;
      createdAt: string;
    }
  | {
      queueId: string;
      kind: "kyc-submission";
      leadRef: string;
      idempotencyKey: string;
      createdAt: string;
    }
  | {
      queueId: string;
      kind: "photo-upload";
      /** Record whose in-memory registry entry holds the File. */
      localId: string;
      /** Server lead id, or localId when the create is still queued. */
      leadRef: string;
      photoType: PhotoWhich;
      createdAt: string;
    };

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / unavailable — queue is best-effort */
  }
}

// ── Lead index API ────────────────────────────────────────────────────────────

export function listLocalLeads(): LocalLeadRecord[] {
  return read<LocalLeadRecord[]>(LEADS_KEY, []);
}

export function getLocalLead(localId: string): LocalLeadRecord | undefined {
  return listLocalLeads().find((l) => l.localId === localId);
}

export function findLocalLeadByServerId(serverId: number): LocalLeadRecord | undefined {
  return listLocalLeads().find((l) => l.serverId === serverId);
}

export function upsertLocalLead(record: LocalLeadRecord): void {
  const leads = listLocalLeads();
  const idx = leads.findIndex((l) => l.localId === record.localId);
  if (idx >= 0) leads[idx] = record;
  else leads.unshift(record);
  write(LEADS_KEY, leads);
}

export function newLocalId(): string {
  return `local-${crypto.randomUUID()}`;
}

// ── Queue API ─────────────────────────────────────────────────────────────────

export function listQueue(): QueuedMutation[] {
  return read<QueuedMutation[]>(QUEUE_KEY, []);
}

export function enqueue(item: QueuedMutation): void {
  write(QUEUE_KEY, [...listQueue(), item]);
}

export function dequeue(queueId: string): void {
  write(
    QUEUE_KEY,
    listQueue().filter((q) => q.queueId !== queueId),
  );
}

export function queueCount(): number {
  return listQueue().length;
}

// ── Photo helpers ─────────────────────────────────────────────────────────────

/**
 * In-memory File registry (live session only).
 * LIMITATION (documented per TASK-007b): localStorage cannot persist File
 * objects, so photo bytes survive only within the live browser session.
 * After a reload, queued photo uploads stay queued and the per-photo state
 * shows "failed" until the rep recaptures the photo on S1. Metadata (name,
 * state, url) does persist.
 */
const photoFiles = new Map<string, File>();

function photoKey(localId: string, which: PhotoWhich): string {
  return `${localId}:${which}`;
}

export function registerPhotoFile(localId: string, which: PhotoWhich, file: File): void {
  photoFiles.set(photoKey(localId, which), file);
}

export function getPhotoFile(localId: string, which: PhotoWhich): File | undefined {
  return photoFiles.get(photoKey(localId, which));
}

/** Tolerate legacy records where photos were stored as bare file names. */
export function normalisePhoto(p: PhotoCapture | string | undefined): PhotoCapture | undefined {
  if (!p) return undefined;
  if (typeof p === "string") return { name: p, state: "pending" };
  return p;
}

export function normalisePhotos(photos: LocalLeadRecord["photos"]): LeadPhotos | undefined {
  if (!photos) return undefined;
  const legacy = photos as unknown as { customer?: PhotoCapture | string; house?: PhotoCapture | string };
  return {
    customer: normalisePhoto(legacy.customer),
    house: normalisePhoto(legacy.house),
  };
}

/** Update one photo's upload state (and url) on a local record. */
export function setPhotoState(
  localId: string,
  which: PhotoWhich,
  state: PhotoUploadState,
  url?: string,
): void {
  const record = getLocalLead(localId);
  if (!record) return;
  const photos = normalisePhotos(record.photos) ?? {};
  const current = photos[which];
  if (!current) return;
  photos[which] = { ...current, state, ...(url ? { url } : {}) };
  upsertLocalLead({ ...record, photos });
}
